import React from 'react';
import DashboardTabsHeader from '../../Components/DashboardTabsHeader';
import {
    checkTabPermission,
    getTabPopupPosition,
} from './templateDetailsHelpers';
import TemplateDashboardAddTabTrigger from './TemplateDashboardAddTabTrigger';
import TemplateDashboardEditControls from './TemplateDashboardEditControls';
import TemplateDashboardTabItem from './TemplateDashboardTabItem';
import { normalizeWebDashboardLayoutMode } from '../../utils/webDashboardLayout';

const TemplateDashboardTabsHeader = ({
    t,
    details,
    edit,
    userRole,
    currentDashboard,
    setCurrentDashboard,
    setSearchParams,
    setDetails,
    editingTabRoleData,
    setEditingTabRoleData,
    editTriggerRef,
    setEditPopupAnchor,
    addTriggerRef,
    setAddPopupAnchor,
    setAddDashboard,
    canReorderDashboardTabs,
    isDashboardExpandedActive,
    webDashboardT,
    isMultiSelectEnabled,
    handleMultiSelectDashboardToggle,
    isMultiSelectToggleReady,
    isCurrentDashboardFixedWidth,
    handleFixedWidthDashboardToggle,
    isFixedWidthToggleReady,
    setIsDashboardExpanded,
    dashboardExpandLabel,
}) => (
    <DashboardTabsHeader
        className={`template-dashboard-tabs-header${isDashboardExpandedActive ? ' dashboard-edit-expanded-tabs-header' : ''}`}
        tabsClassName="dashboard-tabs template-dashboard-tabs-scroll"
        tabsStyle={{ gap: '12px' }}
        onDrop={(e) => {
            if (edit.mode !== 'edit' || !canReorderDashboardTabs) {
                return;
            }

            const dragIdx = parseInt(e.dataTransfer.getData('text/plain')),
                targetTabElement = e.target.closest('.dashboard-tab');
            if (!targetTabElement) return;
            const dropIdx = parseInt(targetTabElement.dataset.originalIndex);

            if (dragIdx >= 0 && dropIdx >= 0 && dragIdx !== dropIdx) {
                setDetails((prevDetails) => {
                    const wdb = [...(prevDetails.webdashboard || [])];
                    [wdb[dragIdx], wdb[dropIdx]] = [wdb[dropIdx], wdb[dragIdx]];
                    return { ...prevDetails, webdashboard: wdb };
                });
                setCurrentDashboard(dropIdx);
                setSearchParams((prev) => {
                    prev.set('dashboardIdx', dropIdx.toString());
                    return prev;
                }, { replace: true });
            }
        }}
        onDragOver={(e) => {
            if (edit.mode === 'edit' && canReorderDashboardTabs) {
                e.preventDefault();
            }
        }}
        actions={(
            <TemplateDashboardEditControls
                edit={edit}
                webDashboardT={webDashboardT}
                isMultiSelectEnabled={isMultiSelectEnabled}
                handleMultiSelectDashboardToggle={handleMultiSelectDashboardToggle}
                isMultiSelectToggleReady={isMultiSelectToggleReady}
                isCurrentDashboardFixedWidth={isCurrentDashboardFixedWidth}
                handleFixedWidthDashboardToggle={handleFixedWidthDashboardToggle}
                isFixedWidthToggleReady={isFixedWidthToggleReady}
                isDashboardExpandedActive={isDashboardExpandedActive}
                setIsDashboardExpanded={setIsDashboardExpanded}
                dashboardExpandLabel={dashboardExpandLabel}
            />
        )}
    >
        {details.webdashboard?.map((wd, idx) => {
            const canView = checkTabPermission(userRole, wd.access_role);
            if (!canView) return null;

            const isActive = idx === currentDashboard;
            const isPopupOpen = editingTabRoleData?.index === idx;

            return (
                <TemplateDashboardTabItem
                    key={wd.id || idx}
                    wd={wd}
                    idx={idx}
                    edit={edit}
                    isActive={isActive}
                    canReorderDashboardTabs={canReorderDashboardTabs}
                    dragLabel={t.dragchangeorder || 'Drag to reorder tab'}
                    editRolesLabel={t.editDashboardRoles || 'Edit dashboard roles'}
                    onSelect={() => {
                        if (isPopupOpen) return;
                        if (idx === currentDashboard) {
                            setEditingTabRoleData(null);
                            return;
                        }

                        setCurrentDashboard(idx);
                        setSearchParams(
                            (prev) => {
                                prev.set('dashboardIdx', idx.toString());
                                return prev;
                            },
                            { replace: true }
                        );
                        setEditingTabRoleData(null);
                    }}
                    onOpenSettings={(event) => {
                        editTriggerRef.current = event.currentTarget;
                        const rect = event.currentTarget.getBoundingClientRect();
                        setEditPopupAnchor(getTabPopupPosition(rect, { width: 320, estimatedHeight: 560 }));

                        setEditingTabRoleData((prev) => {
                            if (prev?.index === idx) return null;
                            return {
                                index: idx,
                                name: wd?.name || '',
                                roles: Array.isArray(wd.access_role) ? [...wd.access_role] : [],
                                layoutMode: normalizeWebDashboardLayoutMode(wd?.layoutMode),
                            };
                        });
                    }}
                />
            );
        })}

        <div className="dashboard-tab-border"></div>
        {edit.mode === 'edit' ? (
            <TemplateDashboardAddTabTrigger
                title={t.dashboardAddTab}
                addTriggerRef={addTriggerRef}
                onOpen={(target) => {
                    const rect = target.getBoundingClientRect();
                    setAddPopupAnchor(getTabPopupPosition(rect, { width: 340, estimatedHeight: 640 }));

                    setAddDashboard((prev) => ({
                        ...prev,
                        adding: true,
                    }));
                }}
            />
        ) : null}
    </DashboardTabsHeader>
);

export default TemplateDashboardTabsHeader;
